import { Switch } from "@material-ui/core"
import Accordion from "@material-ui/core/Accordion"
import AccordionDetails from "@material-ui/core/AccordionDetails"
import AccordionSummary from "@material-ui/core/AccordionSummary"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogContentText from "@material-ui/core/DialogContentText"
import DialogTitle from "@material-ui/core/DialogTitle"
import TextareaAutosize from "@material-ui/core/TextareaAutosize"
import Typography from "@material-ui/core/Typography"
import axios from "axios"
import { useEffect, useState } from "react"
import { SERVER_URI } from "../utility/config"

const OrderTracking = ({ getForwardOrders, orderQueue, getCancelOrders, dispatch, orderList, customerList }) => {
  const storage = window.localStorage
  const userToken = storage.getItem("admin_token")
  const [defaultRejectionMessage, setDefaultRejectionMessage] = useState("")
  const [defaultCancellationMessage, setDefaultCancellationMessage] = useState("")

  // Fetch customers and orders
  useEffect(() => {
    const fetchVehicleAndDriverData = async (orderId) => {
      try {
        const response = await axios.get(`${SERVER_URI}/get_vehicle_and_driver_data/${orderId}`, {
          headers: { Authorization: `Token ${userToken}` },
        })
        return response.data
      } catch (error) {
        console.error("Error fetching vehicle and driver data.", error)
      }
    }
    const fetchOrderQueue = async () => {
      let orderQueue
      try {
        orderQueue = await axios.get(`${SERVER_URI}/get_order_queue/`, {
          headers: { Authorization: `Token ${userToken}` },
        })
        if (orderQueue) {
          dispatch({
            type: "SET_ORDER_QUEUE",
            data: {
              orderQueue: orderQueue.data,
            },
          })
        }
      } catch (err) {
        console.log("Error in fetching order queue", err)
      }
    }
    const fetchOrders = async () => {
      let ordersResponse
      try {
        ordersResponse = await axios.get(`${SERVER_URI}/list_orders/`, {
          headers: { Authorization: `Token ${userToken}` },
        })
        // Add driver and vehicle information to orders where state is underway
        // Create an array of promises for orders that are underway
        const orderPromises = ordersResponse.data.map(async (order) => {
          if (order.state === "underway") {
            const data = await fetchVehicleAndDriverData(order.orderId)
            order.assignedVehicle = data.vehicle
            order.assignedDriver = data.driver
          }
          return order
        })

        const orders = await Promise.all(orderPromises)

        if (orders) {
          dispatch({
            type: "REFRESH_DATA",
            data: { customerOrders: orders },
          })
        }
      } catch (err) {
        console.log("Error in fetching orders", err)
      }
    }
    const fetchCustomers = async () => {
      let customers
      try {
        customers = await axios.get(`${SERVER_URI}/list_customers/`, {
          headers: { Authorization: `Token ${userToken}` },
        })
        if (customers) {
          dispatch({
            type: "REFRESH_DATA",
            data: { customers: customers.data },
          })
        }
      } catch (err) {
        console.log("Error in fetching customers", err)
      }
    }
    const fetchDefaultRejectionMessage = async () => {
      try {
        const response = await axios.get(`${SERVER_URI}/get_default_reject_message/`, {
          headers: { Authorization: `Token ${userToken}` },
        })
        setDefaultRejectionMessage(response.data.rejectMessage)
      } catch (error) {
        console.error("Error fetching default rejection message.", error)
      }
    }
    const fetchDefaultCancellationMessage = async () => {
      try {
        const response = await axios.get(`${SERVER_URI}/get_cancel_message/`, {
          headers: { Authorization: `Token ${userToken}` },
        })
        setDefaultCancellationMessage(response.data)
      } catch (error) {
        console.error("Error fetching default cancellation message.", error)
      }
    }

    console.log("Setting interval")
    const intervalId = setInterval(() => {
      if (userToken) {
        fetchOrderQueue()
        fetchOrders()
        fetchCustomers()
        fetchDefaultRejectionMessage()
        fetchDefaultCancellationMessage()
      }
    }, 2000)
    return () => {
      console.log("Clearing interval")
      clearInterval(intervalId)
    }
  }, [dispatch, userToken])

  const [orderSettings, setOrderSettings] = useState({
    forwardOrders: getForwardOrders,
    cancelOrders: getCancelOrders,
  })

  const handleSwitchChange = (orderType) => {
    setOrderSettings((prevSettings) => {
      let updatedSettings = {
        forwardOrders: prevSettings.forwardOrders,
        cancelOrders: prevSettings.cancelOrders,
      }

      if (orderType === "forward") {
        updatedSettings = {
          forwardOrders: !prevSettings.forwardOrders,
          cancelOrders: false,
        }
      } else if (orderType === "cancel") {
        updatedSettings = {
          forwardOrders: false,
          cancelOrders: !prevSettings.cancelOrders,
        }
      }

      return updatedSettings
    })
  }

  useEffect(() => {
    const handleOrderForwardingAutomation = async () => {
      try {
        await axios.post(
          `${SERVER_URI}/set_forward_orders/`,
          { forwardOrders: orderSettings.forwardOrders },
          { headers: { Authorization: `Token ${userToken}` } }
        )
      } catch (error) {
        console.error("Error changing automation status.", error)
      }
    }

    if (orderSettings.forwardOrders !== undefined) {
      handleOrderForwardingAutomation()
    }
  }, [orderSettings.forwardOrders, userToken])

  useEffect(() => {
    const handleCancelOrders = async () => {
      try {
        await axios.post(
          `${SERVER_URI}/set_cancel_orders/`,
          { cancelOrders: orderSettings.cancelOrders },
          { headers: { Authorization: `Token ${userToken}` } }
        )
      } catch (error) {
        console.error("Error changing cancel orders status.", error)
      }
    }

    if (orderSettings.cancelOrders !== undefined) {
      handleCancelOrders()
    }
  }, [orderSettings.cancelOrders, userToken])

  if (orderList === undefined || customerList === undefined) {
    return <h2>Loading...</h2>
  }
  // Filter out orders which state is not received or underway, and adminJudgement is rejected
  const filteredOrderList = orderList.filter((order) => {
    if (order.state === "received" || order.state === "underway") {
      if (order.adminJudgement === "none" || order.adminJudgement === "accepted") {
        return true
      }
      return false
    } else {
      return false
    }
  })

  // Add customer name to order
  filteredOrderList.forEach((order) => {
    const customer = customerList.find((customer) => customer.customerId === order.ordererId)
    if (customer) {
      order.customerName = customer.callSign
      order.customerPhoneNumber = customer.phoneNumber
    }
  })

  // Add passenger name and number to order
  filteredOrderList.forEach((order) => {
    if (order.ordererId !== order.passengerId) {
      const passenger = customerList.find((customer) => customer.customerId === order.passengerId)
      if (passenger) {
        order.passengerName = passenger.callSign
        order.passengerPhoneNumber = passenger.phoneNumber
      }
    }
  })

  const selectOrderByID = (id, status) => {
    const order = orderList.find((order) => order.orderId === id)
    // Add customer name and passenger name and phonenumber to order
    const customer = customerList.find((customer) => customer.customerId === order.ordererId)
    if (customer) {
      order.customerName = customer.callSign
      order.customerPhoneNumber = customer.phoneNumber
    }
    if (order.ordererId !== order.passengerId) {
      const passenger = customerList.find((customer) => customer.customerId === order.passengerId)
      if (passenger) {
        order.passengerName = passenger.callSign
        order.passengerPhoneNumber = passenger.phoneNumber
      }
    }
    order.queueStatus = status
    return order
  }

  return (
    <>
      {orderQueue !== undefined && (
        <div className="configurations">
          <h3>Tilausten seuranta</h3>
          <Box m={2} width={"calc(100% - 16px)"}>
            {filteredOrderList.length === 0 || filteredOrderList === undefined || filteredOrderList === null ? (
              <h4 key="123">Ei Tilauksia</h4>
            ) : (
              <>
                {filteredOrderList.map((order) => (
                  <SingleOrder
                    key={order.orderId}
                    order={order}
                    dispatch={dispatch}
                    defaultRejectionMessage={defaultRejectionMessage}
                  />
                ))}
              </>
            )}
            <Box mt={4}>
              <h3>Tilausten automaattinen käsittely</h3>
              Automaattinen edelleenlähetys{" "}
              <Switch onChange={() => handleSwitchChange("forward")} checked={orderSettings.forwardOrders} disabled />
              Automaattinen peruminen{" "}
              <Switch onChange={() => handleSwitchChange("cancel")} checked={orderSettings.cancelOrders} />
            </Box>
            {orderQueue.length === 0 || orderQueue === undefined || orderQueue === null ? (
              <h4>Ei tilauksia</h4>
            ) : (
              <Box>
                {orderQueue.map((order) => (
                  <SingleOrderInQueue
                    key={order.id}
                    order={selectOrderByID(order.order_id, order.status)}
                    defaultCancellationMessage={defaultCancellationMessage}
                    dispatch={dispatch}
                    id={order.id}
                  />
                ))}
              </Box>
            )}
          </Box>
        </div>
      )}
    </>
  )
}

const SingleOrder = ({ order, dispatch, defaultRejectionMessage }) => {
  const [rejectRideConfirmation, setRejectRideConfirmation] = useState(false)

  const translateTypeString = (type) => {
    switch (type) {
      case "immediate":
        return "Hetitilaus"
      case "approximate":
        return "Nointilaus"
      default:
        return "Aikatilaus"
    }
  }

  const translateStatusString = (status) => {
    switch (status) {
      case "received":
        return "Vastaanotettu"
      default:
        return "Käynnissä"
    }
  }

  const translatePickUpTime = (time, timeTolerance) => {
    const date = new Date(time)
    const day = date.getDate()
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    const pad = (num) => num.toString().padStart(2, "0")

    if (!timeTolerance) {
      return `${day}.${month}.${year} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    const before = new Date(date.getTime() - timeTolerance * 60 * 1000)
    const after = new Date(date.getTime() + timeTolerance * 60 * 1000)

    return `${day}.${month}.${year} ${pad(before.getHours())}:${pad(before.getMinutes())}-${pad(
      after.getHours()
    )}:${pad(after.getMinutes())}`
  }

  const titleBgColor = order.state === "underway" ? "green" : order.adminJudgement === "accepted" ? "yellow" : "#ff9980"

  const handleAcceptOrder = async () => {
    try {
      await axios.post(
        `${SERVER_URI}/admin_accept_order/${order.orderId}`,
        { orderId: order.orderId },
        { headers: { Authorization: `Token ${localStorage.getItem("admin_token")}` } }
      )
    } catch (error) {
      console.error("Error accepting order.", error)
    }
    dispatch({ type: "ACCEPTED_ORDER", data: { orderId: order.orderId } })
  }

  const handleRejectOrderOpen = async () => {
    setRejectRideConfirmation(true)
  }

  const handleOrderRejection = async (message) => {
    try {
      await axios.post(
        `${SERVER_URI}/admin_reject_order/${order.orderId}`,
        { orderId: order.orderId, message: message },
        { headers: { Authorization: `Token ${localStorage.getItem("admin_token")}` } }
      )
    } catch (error) {
      console.error("Error rejecting order.", error)
    }
    dispatch({ type: "REJECTED_ORDER", data: { orderId: order.orderId } })
    setRejectRideConfirmation(false)
  }

  const handleMoveOrderToQueue = async () => {
    let res
    try {
     res = await axios.post(
        `${SERVER_URI}/admin_move_order_to_queue/${order.orderId}`,
        { orderId: order.orderId },
        { headers: { Authorization: `Token ${localStorage.getItem("admin_token")}` }}
      )
    } catch (error) {
      console.error("Error moving order to queue.", error)
    }
    console.log("res.data", res.data)
    dispatch({ type: "MOVE_ORDER_TO_QUEUE", data: res.data })
    dispatch({ type: "UPDATE_ORDER_STATE", data:{ newOrderState:{orderId: order.orderId, state: "pending_forward"}} })
  }

  const handleCancelOrder = async () => {
    try {
      await axios.put(
        `${SERVER_URI}/admin_cancel_order_by_customer/${order.orderId}`,
        { orderId: order.orderId },
        { headers: { Authorization: `Token ${localStorage.getItem("admin_token")}` }
      })
    } catch (error) {
      console.error("Error cancelling order.", error)
    }
    dispatch({ type: "CANCELLED_ORDER", data: { orderId: order.orderId } })
  }

  
  return (
    <>
      <Accordion>
        <AccordionSummary aria-controls="panel1a-content" id="panel1a-header" style={{ backgroundColor: titleBgColor }}>
          <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
            <Box display={"flex"} flexDirection={"row"}>
              <h4>{translateTypeString(order.orderType)}</h4>
              <h4>{translatePickUpTime(order.pickUpTime, order.timeTolerance)}</h4>
            </Box>
            <Box display={"flex"} flexDirection={"row"}>
              {order.state === "underway" && (
                <h4>
                  {order.assignedDriver.name} {order.assignedVehicle.registerPlate}
                </h4>
              )}
              {order.adminJudgement === "none" ? <h4>{translateStatusString(order.state)}</h4> : <h4>Hyväksytty</h4>}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width={"100%"} display={"flex"} flexDirection={"column"}>
            <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
              <Box
                pl={4}
                width={"100%"}
                display={"flex"}
                flexDirection={"column"}
                textAlign={"left"}
                justifyContent={"right"}
                alignItems={"right"}
              >
                <Typography variant="body1">Tilaaja: {order.customerName}</Typography>
                <Typography variant="body1">Puhelinnumero: {order.customerPhoneNumber}</Typography>
                {order.ordererId !== order.passengerId && (
                  <>
                    <Typography variant="body1">Matkustaja: {order.passengerName}</Typography>
                    <Typography variant="body1">Puhelinnumero: {order.passengerPhoneNumber}</Typography>
                  </>
                )}
              </Box>
              <Box pr={4} width={"100%"} display={"flex"} flexDirection={"column"} textAlign={"left"}>
                <Box display={"flex"} flexDirection={"column"} textAlign={"left"}>
                  <Typography variant="body1">Lähtöpaikka: {order.pickUpLocation}</Typography>
                  <Typography variant="body1">Määränpää: {order.targetLocation}</Typography>
                  <Typography variant="body1">Matkustajat: {order.passengerCount}</Typography>
                  <Typography variant="body1">
                    Lähtöaika: {translatePickUpTime(order.pickUpTime, order.timeTolerance)}
                  </Typography>
                  <Typography variant="body1">Alennusprosentti: {order.discountRate}</Typography>
                </Box>
              </Box>
            </Box>
            {order.orderType !== "immediate" ? (
              <Box
                width={"100%"}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                style={{ gap: "16px" }}
              >
                <Button
                  variant="contained"
                  style={{ backgroundColor: "red" }}
                  fullWidth
                  onClick={handleRejectOrderOpen}
                >
                  Hylkää
                </Button>
                {order.adminJudgement === "none" && (
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "green" }}
                    fullWidth
                    onClick={handleAcceptOrder}
                    disabled={order.adminJudgement === "accepted"}
                  >
                    Hyväksy
                  </Button>
                )}
              </Box>
            ) : (
              <Box>
                {order.state !== "underway" ? (
                <Button variant="contained" style={{ backgroundColor: "red" }} fullWidth onClick={handleMoveOrderToQueue}>
                  Peru/Välitä eteenpäin
                </Button>
                ):
                (
                  <Button variant="contained" style={{ backgroundColor: "red" }} fullWidth onClick={handleCancelOrder}> Peru </Button>
                )
                }
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      <RejectRideConfirmation
        rejectRideConfirmation={rejectRideConfirmation}
        setRejectRideConfirmation={setRejectRideConfirmation}
        defaultRejectionMessage={defaultRejectionMessage}
        handleOrderRejection={handleOrderRejection}
      />
    </>
  )
}

const SingleOrderInQueue = ({ order, defaultCancellationMessage, dispatch, id }) => {
  const [cancelRideConfirmation, setCancelRideConfirmation] = useState(false)
  const [deleteRideConfirmation, setDeleteRideConfirmation] = useState(false)
  // Return time difference between now and pick up time in HH:MM:SS format with leading zeros and without - sign
  const calculateTimeDifferenceFromNowToPickUpTime = (time) => {
    const date = new Date(time)
    const now = new Date()
    const diff = now - date
    const hours = Math.floor(diff / 1000 / 60 / 60)
    const minutes = Math.floor(diff / 1000 / 60 - hours * 60)

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
  }

  const handleCancelOrderOpen = async () => {
    setCancelRideConfirmation(true)
  }

  const handleDeleteOrderOpen = async () => {
    setDeleteRideConfirmation(true)
  }

  const handleOrderCancellation = async (message) => {
    // Endpoint for cancelling order is /admin_cancel_order/:orderId with message in body
    try {
      await axios.post(
        `${SERVER_URI}/admin_cancel_order/${order.orderId}`,
        { orderId: order.orderId, message: message },
        { headers: { Authorization: `Token ${localStorage.getItem("admin_token")}` } }
      )
    } catch (error) {
      console.error("Error cancelling order.", error)
    }
    dispatch({ type: "REMOVE_ORDER_FROM_QUEUE", data: { id: id } })
    setCancelRideConfirmation(false)
  }

  const handleOrderDeletion = async () => {
    // Endpoint for deleting order is /admin_delete_order/:orderId
    try {
      await axios.post(
        `${SERVER_URI}/admin_delete_order/${order.orderId}`,
        { orderId: order.orderId },
        { headers: { Authorization: `Token ${localStorage.getItem("admin_token")}` } }
      )
    } catch (error) {
      console.error("Error deleting order.", error)
    }
    dispatch({ type: "REMOVE_ORDER_FROM_QUEUE", data: { id: id } })
    setDeleteRideConfirmation(false)
  }

  return (
    <>
      <Accordion>
        <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
          <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
            <Box display={"flex"} flexDirection={"row"}>
              <h4>{order.orderId}</h4>
            </Box>
            <Box>
              <h4>{order.pickUpLocation}</h4>
            </Box>
            <Box>
              <h4>{calculateTimeDifferenceFromNowToPickUpTime(order.pickUpTime)}</h4>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails style={{ borderTop: "1px solid black" }}>
          <Box width={"100%"} display={"flex"} flexDirection={"column"}>
            <Box width={"100%"}>
              <h4>Tila:{order.queueStatus}</h4>
            </Box>
            <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
              <Box pl={4} display={"flex"} flexDirection={"column"} textAlign={"left"}>
                <Typography variant="body1">Tilaaja: {order.customerName}</Typography>
                <Typography variant="body1">Puhelinnumero: {order.customerPhoneNumber}</Typography>
                {order.ordererId !== order.passengerId && (
                  <>
                    <Typography variant="body1">Matkustaja: {order.passengerName}</Typography>
                    <Typography variant="body1">Puhelinnumero: {order.passengerPhoneNumber}</Typography>
                  </>
                )}
              </Box>
              <Box pr={4}>
                <Box display={"flex"} flexDirection={"column"} textAlign={"left"}>
                  <Typography variant="body1">Lähtöpaikka: {order.pickUpLocation}</Typography>
                  <Typography variant="body1">Määränpää: {order.targetLocation}</Typography>
                  <Typography variant="body1">Matkustajat: {order.passengerCount}</Typography>
                </Box>
              </Box>
            </Box>
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              style={{ gap: "16px" }}
            >
              <Button variant="contained" style={{ backgroundColor: "red" }} fullWidth onClick={handleDeleteOrderOpen}>
                Poista tilaus
              </Button>
              <Button variant="contained" style={{ backgroundColor: "red" }} fullWidth onClick={handleCancelOrderOpen}>
                Peru tilaus
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      <CancelRideConfirmation
        cancelRideConfirmation={cancelRideConfirmation}
        setCancelRideConfirmation={setCancelRideConfirmation}
        defaultCancellationMessage={defaultCancellationMessage}
        handleOrderCancellation={handleOrderCancellation}
      />
      <DeleteRideConfirmation
        deleteRideConfirmation={deleteRideConfirmation}
        setDeleteRideConfirmation={setDeleteRideConfirmation}
        handleOrderDeletion={handleOrderDeletion}
      />
    </>
  )
}

const RejectRideConfirmation = ({
  rejectRideConfirmation,
  setRejectRideConfirmation,
  defaultRejectionMessage,
  handleOrderRejection,
}) => {
  const [message, setMessage] = useState(defaultRejectionMessage || "")

  return (
    <Dialog
      open={rejectRideConfirmation}
      onClose={() => setRejectRideConfirmation(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Haluatko varmasti hylätä tilauksen?"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Viesti asiakkaalle:
          <br />
          <TextareaAutosize
            className={"message"}
            aria-label="empty textarea"
            placeholder="Viesti"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions width="100%">
        <Box width="100%" display="flex" justifyContent="space-between" style={{ gap: "8px" }}>
          <Button fullWidth variant="contained" onClick={() => setRejectRideConfirmation(false)} color="primary">
            Peruuta
          </Button>
          <Button fullWidth variant="contained" onClick={() => handleOrderRejection(message)} color="primary" autoFocus>
            Hylkää
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

const CancelRideConfirmation = ({
  cancelRideConfirmation,
  setCancelRideConfirmation,
  defaultCancellationMessage,
  handleOrderCancellation,
}) => {
  const [message, setMessage] = useState(defaultCancellationMessage || "")

  return (
    <Dialog
      open={cancelRideConfirmation}
      onClose={() => setCancelRideConfirmation(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Haluatko varmasti peruuttaa tilauksen?"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Viesti asiakkaalle:
          <br />
          <TextareaAutosize
            className={"message"}
            aria-label="empty textarea"
            placeholder="Viesti"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions width="100%">
        <Box width="100%" display="flex" justifyContent="space-between" style={{ gap: "8px" }}>
          <Button onClick={() => setCancelRideConfirmation(false)} color="primary">
            Peruuta
          </Button>
          <Button onClick={() => handleOrderCancellation(message)} color="primary" autoFocus>
            Peruuta tilaus
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

const DeleteRideConfirmation = ({ deleteRideConfirmation, setDeleteRideConfirmation, handleOrderDeletion }) => {
  return (
    <Dialog
      open={deleteRideConfirmation}
      onClose={() => {}}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Haluatko varmasti poistaa tilauksen?"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">Tilausta ei voi enää palauttaa.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setDeleteRideConfirmation(false)
          }}
          color="primary"
        >
          Peruuta
        </Button>
        <Button onClick={handleOrderDeletion} color="primary" autoFocus>
          Poista tilaus
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default OrderTracking
